// Guide Mode Context — Persistent AI copilot that teaches the ONE system operating loop
// Tracks page context, milestones, suggested actions, and page state signals

import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { get } from '../../lib/api';
import { useQuery } from '../../lib/hooks';

// ── Types ──

export interface SuggestedAction {
  id: string;
  label: string;
  description: string;
  route?: string;
  target?: string; // data-tour selector
  prerequisite?: string; // milestone ID — hide/checkmark when done
  loopStep: number; // 1-10 which loop step this action serves
}

export interface PageGuide {
  title: string;
  description: string;
  loopSteps: number[]; // which steps of the 10-step loop this page serves
  suggestedActions: SuggestedAction[];
  emptyStateHint?: string; // shown when page has no data
  dataStateHint?: string; // shown when page has data
}

export interface PageState {
  hasData?: boolean;
  [key: string]: unknown;
}

interface Milestone {
  id: string;
  label: string;
  description: string;
  category: string;
  count: number;
}

interface OnboardingProgress {
  overall_percent: number;
  completed_count: number;
  total_milestones: number;
  is_complete: boolean;
  current_phase: string;
  completed: Milestone[];
  pending: Milestone[];
  next_action: Milestone | null;
}

interface ProgressResponse {
  success: boolean;
  progress: OnboardingProgress;
}

export interface GuideModeContextValue {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
  currentGuide: PageGuide | null;
  currentPath: string;
  pageState: PageState;
  setPageState: (state: PageState) => void;
  milestones: OnboardingProgress | null;
  isMilestoneComplete: (milestoneId: string) => boolean;
  completedMilestoneIds: string[];
  overallPercent: number;
}

// ── Loop step labels ──
export const LOOP_STEPS = [
  'Data In',
  'Identity Resolution',
  'Intelligence Scoring',
  'Policy Evaluation',
  'Workflow Synthesis',
  'Agent Execution',
  'Channel Delivery',
  'Decision Tracing',
  'Lift Measurement',
  'Feedback Loop',
] as const;

// ── Page Guide Registry ──

export const PAGE_GUIDES: Record<string, PageGuide> = {
  // ── Command Center ──
  '/': {
    title: 'Command Center',
    description: 'Your operational cockpit. See real-time KPIs, AI briefings, pending actions, and system activity. Everything starts here.',
    loopSteps: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    suggestedActions: [
      { id: 'review-briefing', label: 'Review AI Briefing', description: 'Read the AI-generated operational summary', target: '[data-tour="cc-tab-briefings"]', loopStep: 9 },
      { id: 'check-queue', label: 'Check Action Queue', description: 'Review pending agent actions', target: '[data-tour="cc-tab-action-queue"]', loopStep: 6 },
      { id: 'import-contacts', label: 'Import Contacts', description: 'Add your first contacts to start building intelligence', route: '/crm/contacts', prerequisite: 'contacts_imported', loopStep: 1 },
    ],
    emptyStateHint: 'Your Command Center will light up as you add data and activate agents. Start by importing contacts.',
    dataStateHint: 'Your system is generating intelligence. Review the briefing for actionable insights.',
  },
  '/dashboard': {
    title: 'Command Center',
    description: 'Your operational cockpit — alias for the main dashboard.',
    loopSteps: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    suggestedActions: [],
  },

  // ── CRM ──
  '/crm/contacts': {
    title: 'Contacts',
    description: 'Your unified contact database. Every person the system tracks. Adding contacts is Step 1 of the operating loop.',
    loopSteps: [1, 2],
    suggestedActions: [
      { id: 'create-contact', label: 'New Contact', description: 'Add a contact to start building intelligence', target: '[data-tour="new-contact"]', prerequisite: 'contacts_imported', loopStep: 1 },
      { id: 'export-contacts', label: 'Export CSV', description: 'Download your contacts as a CSV file', target: '[data-tour="contacts-export"]', loopStep: 1 },
      { id: 'connect-email', label: 'Connect Email', description: 'Verify a sender domain to enable outreach', route: '/settings/communication', prerequisite: 'email_connected', loopStep: 7 },
    ],
    emptyStateHint: 'No contacts yet. Click "New Contact" to add your first — this activates the intelligence engine.',
    dataStateHint: 'Contacts are the foundation. Try segmenting by churn risk or creating a smart list.',
  },
  '/crm/companies': {
    title: 'Companies',
    description: 'Organization-level view. Companies link to contacts and deals, aggregating intelligence across people.',
    loopSteps: [1, 2],
    suggestedActions: [
      { id: 'create-company', label: 'New Company', description: 'Create a company record', target: '[data-tour="new-company"]', loopStep: 1 },
    ],
    emptyStateHint: 'No companies yet. Create one and link contacts to it for organizational intelligence.',
    dataStateHint: 'Companies aggregate intelligence from all linked contacts. Click any company to see its full profile.',
  },
  '/crm/pipeline': {
    title: 'Pipeline',
    description: 'Deal tracking with Kanban visualization. Deals feed into workflow synthesis — the system generates actions to move them forward.',
    loopSteps: [1, 5, 6],
    suggestedActions: [
      { id: 'create-deal', label: 'New Deal', description: 'Create a pipeline deal', target: '[data-tour="new-deal"]', prerequisite: 'pipeline_created', loopStep: 1 },
    ],
    emptyStateHint: 'No deals yet. Create your first deal to activate pipeline intelligence and AI-powered deal progression.',
    dataStateHint: 'Deals with high probability will get AI-drafted outreach emails. Move deals through stages to train the system.',
  },
  '/conversations': {
    title: 'Conversations',
    description: 'Email threads and communication history. This is where execution results appear — emails sent and replies received.',
    loopSteps: [6, 7],
    suggestedActions: [
      { id: 'send-email', label: 'Send First Email', description: 'Compose and send your first email', prerequisite: 'first_email_sent', loopStep: 7 },
    ],
    emptyStateHint: 'No conversations yet. Send your first email to start building communication intelligence.',
    dataStateHint: 'Every email feeds the intelligence engine. Response patterns improve future timing and content.',
  },

  // ── Intelligence ──
  '/intelligence': {
    title: 'Intelligence Hub',
    description: 'The brain of the system. Six-dimension behavioral intelligence scores every customer continuously from real interaction data.',
    loopSteps: [3, 9, 10],
    suggestedActions: [
      { id: 'view-revenue', label: 'Revenue Tab', description: 'See revenue attribution and pipeline analytics', target: '[data-tour="intelligence-tab-revenue"]', loopStep: 9 },
      { id: 'view-agents-perf', label: 'Agent Performance', description: 'See how AI agents are performing', target: '[data-tour="intelligence-tab-agents"]', loopStep: 9 },
      { id: 'ask-advisor', label: 'Ask LLM Advisor', description: 'Ask strategic questions powered by your data', target: '[data-tour="intelligence-tab-llm"]', loopStep: 3 },
    ],
    emptyStateHint: 'Intelligence builds automatically as you add contacts and interactions. Import data first.',
    dataStateHint: 'Intelligence is active. Explore tabs to see revenue attribution, predictions, and agent performance.',
  },
  '/intelligence/dossiers': {
    title: 'Customer Dossiers',
    description: 'Deep customer profiles with full behavioral intelligence across all six dimensions.',
    loopSteps: [3],
    suggestedActions: [],
  },
  '/intelligence/segments': {
    title: 'Segments',
    description: 'AI-powered customer segmentation based on behavioral patterns and intelligence scores.',
    loopSteps: [3, 5],
    suggestedActions: [],
  },
  '/intelligence/predictions': {
    title: 'Predictions',
    description: 'Predictive models for churn, conversion, and lifetime value.',
    loopSteps: [3, 9],
    suggestedActions: [],
  },
  '/intelligence/analytics': {
    title: 'Analytics',
    description: 'Campaign and interaction analytics with causal lift measurement.',
    loopSteps: [9],
    suggestedActions: [],
  },
  '/intelligence/observatory': {
    title: 'Observatory',
    description: 'Real-time monitoring of intelligence signals and anomaly detection.',
    loopSteps: [3, 10],
    suggestedActions: [],
  },
  '/intelligence/ab-tests': {
    title: 'A/B Tests',
    description: 'Experiment management for measuring causal lift of different strategies.',
    loopSteps: [9, 10],
    suggestedActions: [],
  },
  '/intelligence/reports': {
    title: 'Reports',
    description: 'Generated reports from intelligence data — exportable and shareable.',
    loopSteps: [9],
    suggestedActions: [],
  },

  // ── Execution ──
  '/execution': {
    title: 'Execution Engine',
    description: 'Where AI agents do work. 20+ agents synthesize workflows and execute them under bounded autonomy.',
    loopSteps: [5, 6, 7],
    suggestedActions: [
      { id: 'view-agents', label: 'View Agents', description: 'See all agent functions and their status', target: '[data-tour="execution-tab-agents"]', loopStep: 6 },
      { id: 'view-synthesis', label: 'Workflow Synthesis', description: 'See AI-generated action plans', target: '[data-tour="execution-tab-synthesis"]', loopStep: 5 },
      { id: 'view-logs', label: 'System Logs', description: 'Detailed execution traces for every action', target: '[data-tour="execution-tab-system-logs"]', loopStep: 8 },
    ],
    emptyStateHint: 'Agents activate when you have contacts and configured channels. Complete setup first.',
    dataStateHint: 'Agents are executing. Check System Logs for detailed traces of every action taken.',
  },
  '/execution/agents': {
    title: 'AI Agents',
    description: 'All agent functions with real-time status, execution counts, and performance metrics.',
    loopSteps: [6],
    suggestedActions: [],
  },
  '/execution/workflows': {
    title: 'Workflows',
    description: 'Orchestrated multi-step workflows combining multiple agents.',
    loopSteps: [5, 6],
    suggestedActions: [
      { id: 'trigger-synthesis', label: 'Trigger Synthesis', description: 'Manually trigger AI workflow generation from customer intelligence', target: '[data-tour="trigger-synthesis"]', prerequisite: 'workflow_created', loopStep: 5 },
    ],
  },
  '/execution/comms': {
    title: 'Communications',
    description: 'All outbound communications — emails, SMS, voice calls — with delivery status.',
    loopSteps: [7],
    suggestedActions: [],
  },
  '/execution/meetings': {
    title: 'Meetings',
    description: 'Scheduled and completed meetings managed by AI agents.',
    loopSteps: [6, 7],
    suggestedActions: [],
  },
  '/execution/campaigns': {
    title: 'Campaigns',
    description: 'Campaign execution tracking with real-time performance.',
    loopSteps: [5, 6, 7],
    suggestedActions: [],
  },

  // ── Governance ──
  '/governance': {
    title: 'Governance & Audit',
    description: 'Every action traced. SHA-256 Merkle-chained event ledger, decision receipts, policy versioning, and compliance.',
    loopSteps: [4, 8, 9],
    suggestedActions: [
      { id: 'view-audit', label: 'Audit Log', description: 'Browse the tamper-evident event ledger', target: '[data-tour="governance-tab-audit"]', loopStep: 8 },
      { id: 'view-compliance', label: 'Compliance', description: 'Review policy versions and consent status', target: '[data-tour="governance-tab-compliance"]', prerequisite: 'governance_configured', loopStep: 4 },
    ],
    emptyStateHint: 'The audit log populates automatically as actions occur. Start executing to see governance in action.',
    dataStateHint: 'Every action is Merkle-chained. Click any event to see the full decision trace.',
  },
  '/governance/decisions': {
    title: 'Decisions',
    description: 'AI decisions requiring review or showing autonomous execution results.',
    loopSteps: [4, 8],
    suggestedActions: [],
  },
  '/governance/audit-log': {
    title: 'Audit Log',
    description: 'Complete tamper-evident event ledger with SHA-256 Merkle chain integrity.',
    loopSteps: [8],
    suggestedActions: [],
  },
  '/governance/policies': {
    title: 'Policies',
    description: 'Governance policies that control what agents can and cannot do.',
    loopSteps: [4],
    suggestedActions: [],
  },
  '/governance/compliance': {
    title: 'Compliance',
    description: 'Compliance status, consent coverage, and regulatory alignment.',
    loopSteps: [4, 8],
    suggestedActions: [],
  },

  // ── Settings ──
  '/settings': {
    title: 'Settings',
    description: 'System configuration — channels, billing, team, security, AI preferences, and compliance.',
    loopSteps: [4, 7],
    suggestedActions: [
      { id: 'setup-channels', label: 'Channel Setup', description: 'Configure email, SMS, and voice channels', target: '[data-tour="settings-communication"]', prerequisite: 'email_connected', loopStep: 7 },
      { id: 'invite-team', label: 'Invite Team', description: 'Add team members and assign roles', target: '[data-tour="settings-team"]', prerequisite: 'team_invited', loopStep: 4 },
    ],
  },
  '/settings/communication': {
    title: 'Communication Settings',
    description: 'Configure outbound channels: email domains, SMS numbers, voice AI, auto-response rules, and message templates.',
    loopSteps: [7],
    suggestedActions: [
      { id: 'setup-email', label: 'Channel Setup', description: 'Verify a sender domain for email', target: '[data-tour="comms-tab-channels"]', prerequisite: 'email_connected', loopStep: 7 },
      { id: 'setup-templates', label: 'Templates', description: 'Create message templates for agents', target: '[data-tour="comms-tab-templates"]', loopStep: 5 },
    ],
    emptyStateHint: 'No channels configured. Start with email domain verification in Channel Setup.',
    dataStateHint: 'Channels are active. Configure auto-response rules and templates to optimize agent output.',
  },
  '/settings/billing': {
    title: 'Billing',
    description: 'Subscription management, usage tracking, and plan details.',
    loopSteps: [],
    suggestedActions: [],
  },
  '/settings/team': {
    title: 'Team Management',
    description: 'Invite members, assign roles, and manage seat allocation.',
    loopSteps: [4],
    suggestedActions: [
      { id: 'invite-member', label: 'Invite Member', description: 'Add a team member', target: '[data-tour="invite-member"]', prerequisite: 'team_invited', loopStep: 4 },
    ],
    emptyStateHint: 'No team members yet. Invite your first team member to collaborate.',
    dataStateHint: 'Team members can be assigned roles with different permission levels.',
  },
  '/settings/security': {
    title: 'Security',
    description: 'Password management, two-factor authentication, and API key management.',
    loopSteps: [4],
    suggestedActions: [],
  },
  '/settings/ai': {
    title: 'AI Settings',
    description: 'LLM preferences, model selection, and agent behavior configuration.',
    loopSteps: [5, 6],
    suggestedActions: [],
  },
  '/settings/compliance': {
    title: 'Compliance Settings',
    description: 'Data retention policies, consent configuration, and regulatory settings.',
    loopSteps: [4],
    suggestedActions: [],
  },
  '/settings/help': {
    title: 'Help & Support',
    description: 'Documentation, support channels, and getting started guides.',
    loopSteps: [],
    suggestedActions: [],
  },

  // ── Atlas ──
  '/atlas': {
    title: 'Atlas AI',
    description: 'Your AI assistant. Ask any question about your data and get answers grounded in real system data.',
    loopSteps: [3, 9],
    suggestedActions: [],
    emptyStateHint: 'Ask Atlas anything — it has access to your entire system data.',
    dataStateHint: 'Atlas can analyze trends, explain scores, and suggest strategies based on your real data.',
  },

  // ── Alerts & Notifications ──
  '/alerts': {
    title: 'Alerts',
    description: 'System alerts by priority. Critical alerts require immediate attention.',
    loopSteps: [9, 10],
    suggestedActions: [],
  },
  '/notifications': {
    title: 'Notifications',
    description: 'Your notification inbox — system events, agent completions, and team activity.',
    loopSteps: [8],
    suggestedActions: [],
  },
  '/action-queue': {
    title: 'Action Queue',
    description: 'Pending agent actions awaiting your approval or execution.',
    loopSteps: [6],
    suggestedActions: [],
  },
  '/briefings': {
    title: 'Briefings',
    description: 'AI-generated operational briefings summarizing system state and recommendations.',
    loopSteps: [9, 10],
    suggestedActions: [],
  },
  '/execution-log': {
    title: 'Execution Log',
    description: 'Complete log of all agent executions with timing, status, and outcomes.',
    loopSteps: [6, 8],
    suggestedActions: [],
  },
  '/inbox': {
    title: 'Inbox',
    description: 'Centralized inbox for all inbound communications and system messages.',
    loopSteps: [7],
    suggestedActions: [],
  },

  // ── Customers ──
  '/customers': {
    title: 'Customers',
    description: 'Unified customer memory — all contacts resolved into living operational entities.',
    loopSteps: [1, 2, 3],
    suggestedActions: [],
  },
  '/identity': {
    title: 'Identity Resolution',
    description: 'Cross-channel entity matching. See how contacts are linked into unified identities.',
    loopSteps: [2],
    suggestedActions: [],
  },

  // ── Documents ──
  '/documents': {
    title: 'Documents',
    description: 'Document management — upload, organize, and reference documents.',
    loopSteps: [1],
    suggestedActions: [],
  },

  // ── Admin ──
  '/admin': {
    title: 'Operator Admin',
    description: 'System administration — tenant management, feature flags, LLM configuration, and platform operations.',
    loopSteps: [4],
    suggestedActions: [],
  },

  // ── Healthcare Vertical ──
  '/healthcare/dashboard': {
    title: 'Healthcare Dashboard',
    description: 'Healthcare-specific operational dashboard with clinical metrics and HIPAA-compliant workflows.',
    loopSteps: [1, 3, 6, 8],
    suggestedActions: [],
  },

  // ── Home Services Vertical ──
  '/home-services/dashboard': {
    title: 'Home Services Dashboard',
    description: 'Home services operational dashboard with scheduling, dispatch, and service metrics.',
    loopSteps: [1, 5, 6],
    suggestedActions: [],
  },
};

// ── Context ──

const GuideModeContext = createContext<GuideModeContextValue | null>(null);

const GUIDE_STORAGE_KEY = 'vaerion_guide_mode';

function loadGuideState(): boolean {
  try {
    const stored = localStorage.getItem(GUIDE_STORAGE_KEY);
    if (stored !== null) return JSON.parse(stored);
  } catch { /* ignore */ }
  return false;
}

export function GuideModeProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(loadGuideState);
  const [pageState, setPageState] = useState<PageState>({});
  const location = useLocation();
  const currentPath = location.pathname;

  // Persist open/close state
  useEffect(() => {
    try {
      localStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(isOpen));
    } catch { /* ignore */ }
  }, [isOpen]);

  // Reset page state on navigation
  useEffect(() => {
    setPageState({});
  }, [currentPath]);

  // Fetch milestone progress
  const { data: progressData } = useQuery<ProgressResponse>(
    () => get('/api/operator/onboarding/progress'), []
  );

  const milestones = progressData?.progress ?? null;

  const completedMilestoneIds = useMemo(
    () => milestones?.completed.map((m: Milestone) => m.id) ?? [],
    [milestones],
  );

  const isMilestoneComplete = useCallback(
    (milestoneId: string) => completedMilestoneIds.includes(milestoneId),
    [completedMilestoneIds],
  );

  // Resolve current page guide — try exact match, then prefix match
  const currentGuide = useMemo(() => {
    if (PAGE_GUIDES[currentPath]) return PAGE_GUIDES[currentPath];
    // Try removing trailing slash
    const trimmed = currentPath.replace(/\/$/, '');
    if (PAGE_GUIDES[trimmed]) return PAGE_GUIDES[trimmed];
    // Prefix match for sub-routes (e.g. /crm/contacts/123 → /crm/contacts)
    const segments = trimmed.split('/');
    while (segments.length > 1) {
      segments.pop();
      const parent = segments.join('/') || '/';
      if (PAGE_GUIDES[parent]) return PAGE_GUIDES[parent];
    }
    return null;
  }, [currentPath]);

  const toggle = useCallback(() => setIsOpen((o: boolean) => !o), []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const overallPercent = milestones?.overall_percent ?? 0;

  return (
    <GuideModeContext.Provider value={{
      isOpen, toggle, open, close,
      currentGuide, currentPath, pageState, setPageState,
      milestones, isMilestoneComplete, completedMilestoneIds, overallPercent,
    }}>
      {children}
    </GuideModeContext.Provider>
  );
}

export function useGuideMode() {
  const ctx = useContext(GuideModeContext);
  if (!ctx) throw new Error('useGuideMode must be used within GuideModeProvider');
  return ctx;
}
